import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Validator that checks if all values in an array control are valid 4-digit year strings/numbers.
 *
 * @returns A validator function that returns an error object if validation fails, or null if valid.
 *
 * The error object has the format:
 * {
 *   yearArray: {
 *     invalidValues: any[], // Array of values that are not valid years
 *     invalidIndices: number[] // Array of indices where invalid values were found
 *   }
 * }
 *
 * @example
 * // In a form group:
 * this.form = this.fb.group({
 *   allowedYears: [[], yearArrayValidator()]
 * });
 */
export function yearArrayValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // If the value is null, undefined, or not an array, consider it valid
    // (use Validators.required separately if you want to enforce non-empty)
    if (!value || !Array.isArray(value)) {
      return null;
    }

    // If the array is empty, consider it valid
    if (value.length === 0) {
      return null;
    }

    // Regex to match exactly 4 digits
    const yearRegex = /^\d{4}$/;

    const invalidValues: any[] = [];
    const invalidIndices: number[] = [];

    value.forEach((item, index) => {
      // Convert to string if it's a number
      const itemStr = typeof item === 'number' ? String(item) : item;

      // Check if it's a string and matches the 4-digit pattern
      const isValid = typeof itemStr === 'string' && yearRegex.test(itemStr);

      if (!isValid) {
        invalidValues.push(item);
        invalidIndices.push(index);
      }
    });

    // If there are invalid values, return an error object
    if (invalidValues.length > 0) {
      return {
        yearArray: {
          invalidValues,
          invalidIndices,
        },
      };
    }

    return null;
  };
}
